// src/app/jobs/job-single/job-single.component.ts
import {
  Component, computed, inject, signal, OnInit, DestroyRef,
  afterNextRender, ViewChild, Renderer2, Inject,
  PLATFORM_ID,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { SeoService } from '@/app/core/seo/seo.service';
import { htmlToText } from '@/app/core/seo/seo.utils';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, of, switchMap } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { YoutubeEmbedPipe } from '@/app/pipes/youtube-embed.pipe';
import { JobPositionPublicDto } from '@/app/models/public';
import { DriverApplicationModalComponent } from '../../applications/driver-application-modal/driver-application-modal.component';
import { MatDialog } from '@angular/material/dialog';
import {
  CdlClassFilterLookup,
  DriverTypeFilterLookup,
  HiringStateFilterLookup,
} from '@/app/models/lookups/job-filters.lookup';
import { SalaryUtils } from '@/app/utils/salary.utils';
import { environment } from '@/environments/environment';

@Component({
  selector: 'app-job-single',
  templateUrl: './job-single.component.html',
  imports: [
    RouterLink,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    YoutubeEmbedPipe,
  ],
})
export class JobSingleComponent implements OnInit {

  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private seo = inject(SeoService);
  private renderer = inject(Renderer2);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private platformId = inject(PLATFORM_ID);

  @Inject(DOCUMENT) private document = inject(DOCUMENT);

  job = signal<JobPositionPublicDto | null>(null);
  loading = signal(true);
  notFound = signal(false);

  shareOpen = signal(false);
  copyLabel = signal('📋 Copy Link');

  // ── Convenience computeds ──────────────────────────────────────────────

  jobDetail = computed(() => this.job()?.job ?? null);
  company = computed(() => this.jobDetail()?.matchedLeadsProduct ?? null);

  logo = computed(() => this.company()?.company?.imageUrl ?? null);
  phone = computed(() => this.company()?.company?.phone ?? null);
  phoneHref = computed(() => this.phone()?.replace(/[^+\d]/g, '') ?? null);

  applyUrl = computed(() => this.job()?.customApplyUrl ?? this.jobDetail()?.customApplyUrl ?? null);
  isActive = computed(() => this.job()?.active ?? true);

  title_ = computed(() =>
    this.jobDetail()?.customSeoTitle ?? this.jobDetail()?.title ?? 'Job Position'
  );

  location = computed(() => {
    const pos = this.job();
    return [
      pos?.city ?? null,
      pos?.hiringState?.abbreviation ?? pos?.state ?? null,
      pos?.zip ?? null,
    ].filter(Boolean).join(', ');
  });

  salary = computed(() =>
    this.job()?.salary ?? this.jobDetail()?.salary ?? null
  );

  cdlClasses = computed(() => {
    const c = this.jobDetail()?.cdlClasses;
    return c?.length ? c.map(x => x.name).join(', ') : null;
  });

  driverTypes = computed(() => {
    const d = this.jobDetail()?.driverTypes;
    return d?.length ? d.map(x => x.name).join(', ') : null;
  });

  freightTypes = computed(() => {
    const f = this.jobDetail()?.freightTypes;
    return f?.length ? f.map(x => x.name).join(', ') : null;
  });

  endorsements = computed(() => {
    const e = this.jobDetail()?.endorsements;
    return e?.length ? e.map(x => x.name).join(', ') : null;
  });

  experience = computed(() => {
    const job = this.jobDetail();
    if (job?.customExperienceLevels && job?.customExperienceTypes?.length) {
      return job.customExperienceTypes.map(e => e.name).join(', ');
    }
    if (job?.experienceType?.name) {
      return job.experienceType.name + ' min.';
    }
    return null;
  });

  description = computed(() =>
    this.job()?.job?.descriptionHtml ?? ''
  );

  companyDescription = computed(() =>
    this.company()?.profileDetails ?? this.company()?.biography ?? ''
  );

  mapLat = computed(() =>
    this.job()?.hiringZip?.lat ?? this.job()?.hiringCity?.lat ?? null
  );

  mapLng = computed(() =>
    this.job()?.hiringZip?.lng ?? this.job()?.hiringCity?.lng ?? null
  );

  mapRadiusMiles = computed(() =>
    this.job()?.radius ? Number(this.job()!.radius) : 50
  );

  mapRadiusMeters = computed(() => this.mapRadiusMiles() * 1609.344);

  googleMapsUrl = computed(() => {
    if (this.mapLat() && this.mapLng()) {
      return `https://www.google.com/maps/search/?api=1&query=${this.mapLat()},${this.mapLng()}`;
    }
    const q = this.location();
    return q ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(q)}` : null;
  });

  similarJobsParams = computed(() => {
    const params: Record<string, string> = {};
    const stateId = this.job()?.hiringState?.id;
    const cdlId = this.jobDetail()?.cdlClasses?.[0]?.id;
    const driverId = this.jobDetail()?.driverTypes?.[0]?.id;

    const stateKey = stateId != null ? HiringStateFilterLookup.fromId(String(stateId))?.key : null;
    const cdlKey = cdlId != null ? CdlClassFilterLookup.fromId(String(cdlId))?.key : null;
    const driverKey = driverId != null ? DriverTypeFilterLookup.fromId(String(driverId))?.key : null;

    if (stateKey) params['hiringState'] = stateKey;
    if (cdlKey) params['cdlClass'] = cdlKey;
    if (driverKey) params['driverType'] = driverKey;
    return params;
  });

  jobUrl = computed(() => {
    const slug = this.company()?.slug ?? '';
    const id = this.job()?.id ?? '';
    return `${this.document.location?.origin ?? ''}/companies/${slug}/jobs/${id}`;
  });

  private mapInitialized = false;

  // ── Lifecycle ──────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.route.paramMap
      .pipe(
        switchMap(params => {
          const slug = params.get('slug') ?? '';
          const jobId = params.get('jobPositionId') ?? '';
          this.loading.set(true);
          this.notFound.set(false);
          this.mapInitialized = false; // reset on each navigation

          return this.http.get<JobPositionPublicDto>(`/api/public/companies/${slug}/jobs/${jobId}`).pipe(
            catchError(() => {
              this.notFound.set(true);
              return of(null);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(job => {
        this.job.set(job);
        this.loading.set(false);

        if (!job) {
          this.seo.setPage({
            title: 'Job Not Found',
            description: 'This job is no longer available on Truck Drivers USA. Search other CDL truck driving jobs.',
          });
        }

        if (job) {
          this.setSeo(job);
          this.injectJsonLd(job);

          if (this.mapLat() && this.mapLng()) {
            setTimeout(() => this.initMapWhenVisible(), 0);
          }
        }
      });
  }


  constructor() {
    afterNextRender(() => {
      // Just react to the signal — no second HTTP call needed
      if (this.job() && this.mapLat() && this.mapLng()) {
        this.initMapWhenVisible();
      }
    });
  }


  // ── SEO ────────────────────────────────────────────────────────────────

  private setSeo(job: JobPositionPublicDto): void {
    const jobDetail = job.job;
    const company = jobDetail?.matchedLeadsProduct;
    const jobTitle = jobDetail?.customSeoTitle || jobDetail?.title || 'CDL Truck Driving Job';
    const city = job.city?.split(',')[0]?.trim();
    const state = job.hiringState?.abbreviation ?? job.state;
    const place = [city, state].filter(Boolean).join(', ');
    const salary = job.salary ?? jobDetail?.salary;

    const summary = [
      `${company?.name ?? 'A trucking company'} is hiring: ${jobTitle}${place ? ` in ${place}` : ''}.`,
      salary ? `Pay: ${salary}.` : '',
      htmlToText(jobDetail?.descriptionHtml),
    ].filter(Boolean).join(' ');

    this.seo.setPage({
      title: company?.name ? `${jobTitle} – ${company.name}` : jobTitle,
      description: summary,
      image: company?.heroImage || company?.company?.imageUrl,
      imageAlt: company?.name,
      path: `/companies/${company?.slug ?? ''}/jobs/${job.id}`,
    });
  }

  // ── JSON-LD ────────────────────────────────────────────────────────────

  private injectJsonLd(job: JobPositionPublicDto): void {
    const existing = this.document.getElementById('job-jsonld');
    if (existing) existing.remove();

    const jobDetail = job.job;
    const company = jobDetail?.matchedLeadsProduct;
    const slug = company?.slug ?? '';

    // Public https origin — works in both SSR and browser
    const baseUrl = this.seo.siteOrigin();
    const pageUrl = `${baseUrl}/companies/${slug}/jobs/${job.id}`;
    const applyUrl = job.customApplyUrl ?? jobDetail?.customApplyUrl ?? pageUrl;

    const plainDescription = htmlToText(jobDetail?.descriptionHtml);

    const datePosted = job.timestampPosted ?? job.timestampCreated ?? jobDetail?.timestampPosted ?? jobDetail?.timestampCreated;
    const dateValidThrough = job?.timestampValidThrough;

    const schema: Record<string, unknown> = {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      'title': jobDetail?.customSeoTitle ?? jobDetail?.title ?? '',
      'description': plainDescription,
      'datePosted': datePosted
        ? new Date(datePosted).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      'validThrough': dateValidThrough
        ? new Date(dateValidThrough).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      'employmentType': 'FULL_TIME',
      'directApply': !!applyUrl,
      'hiringOrganization': {
        '@type': 'Organization',
        'name': company?.company?.name ?? company?.name ?? '',
        'logo': company?.company?.imageUrl ?? '',
        'sameAs': `${baseUrl}/companies/${slug}/`,
      },
      'jobLocation': {
        '@type': 'Place',
        'address': {
          '@type': 'PostalAddress',
          'addressLocality': job.city ?? job.hiringCity?.name ?? '',
          'addressRegion': job.hiringState?.abbreviation ?? job.state ?? '',
          'addressCountry': job.country ?? 'US',
        },
      },
    };

    const salary = job.salary ?? jobDetail?.salary;
    if (salary) {
      schema['baseSalary'] = SalaryUtils.parseCompensationToBaseSalary(salary)?.baseSalary;
    }

    const script = this.renderer.createElement('script');
    script.id = 'job-jsonld';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schema, null, 2);
    this.renderer.appendChild(this.document.head, script);
  }


  // ── Mapbox ─────────────────────────────────────────────────────────────
  private initMapWhenVisible(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (this.mapInitialized) return;

    const el = this.document.getElementById('tdusa-job-map');
    if (!el) return;

    this.mapInitialized = true;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        observer.disconnect();
        this.initMap();
      }
    }, { rootMargin: '200px' });

    observer.observe(el);
  }

  private initMap(): void {
    const lat = this.mapLat()!;
    const lng = this.mapLng()!;
    const radius = this.mapRadiusMeters();
    const miles = this.mapRadiusMiles();
    const zoom = miles <= 25 ? 9 : miles <= 75 ? 7 : 6;

    // Dynamically load Mapbox CSS
    const link = this.renderer.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
    this.renderer.appendChild(this.document.head, link);

    // Dynamically load Mapbox JS then init
    const script = this.renderer.createElement('script');
    script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
    script.onload = () => {
      const mapboxgl = (window as any)['mapboxgl'];
      mapboxgl.accessToken = environment.mapboxAccessToken;

      const map = new mapboxgl.Map({
        container: 'tdusa-job-map',
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [lng, lat],
        zoom,
        interactive: false,
      });

      map.on('load', () => {
        map.addSource('radius-circle', {
          type: 'geojson',
          data: {
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [lng, lat] },
          },
        });

        map.addLayer({
          id: 'radius-fill',
          type: 'circle',
          source: 'radius-circle',
          paint: {
            'circle-radius': { stops: [[0, 0], [20, radius]], base: 2 },
            'circle-color': '#0073aa',
            'circle-opacity': 0.15,
          },
        });

        map.addLayer({
          id: 'radius-border',
          type: 'circle',
          source: 'radius-circle',
          paint: {
            'circle-radius': { stops: [[0, 0], [20, radius]], base: 2 },
            'circle-color': 'transparent',
            'circle-stroke-width': 2,
            'circle-stroke-color': '#0073aa',
            'circle-opacity': 0,
            'circle-stroke-opacity': 0.5,
          },
        });

        new mapboxgl.Marker({ color: '#0073aa' })
          .setLngLat([lng, lat])
          .addTo(map);
      });
    };

    this.renderer.appendChild(this.document.body, script);
  }

  // ── Actions ────────────────────────────────────────────────────────────

  toggleShare(): void {
    this.shareOpen.update(v => !v);
  }

  closeShare(): void {
    this.shareOpen.set(false);
  }

  async copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.jobUrl());
      this.copyLabel.set('✅ Copied!');
      setTimeout(() => {
        this.copyLabel.set('📋 Copy Link');
        this.shareOpen.set(false);
      }, 1500);
    } catch {
      // fallback — silently ignore
    }
  }

  shareUrl(platform: 'facebook' | 'x' | 'sms'): string {
    const url = encodeURIComponent(this.jobUrl());
    const text = encodeURIComponent(`${this.title_()} at ${this.company()?.name ?? ''}`);
    const body = encodeURIComponent(`Check out this job: ${this.jobUrl()}`);

    switch (platform) {
      case 'facebook': return `https://www.facebook.com/sharer/sharer.php?u=${url}`;
      case 'x': return `https://x.com/intent/tweet?url=${url}&text=${text}`;
      case 'sms': return `sms:?&body=${body}`;
    }
  }

  openApplicationModal(): void {
    this.dialog.open(DriverApplicationModalComponent, {
      width: '100%',
      maxWidth: '1024px',
      height: '100dvh',
      panelClass: 'application-modal',
      // Pass data via inputs using the new signal-based input binding
      data: {
        matchedLeadsProduct: this.company(),
        jobPosition: this.job(),
      },
    });
  }
}
